import { Injectable, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }      from '@ngx-translate/core';

import { ApiService }              from '../api/api.service';
import { ImportFormFieldModel }    from './typings/imports.form-field';
import { ImportProgressComponent } from './import.progress.component';
import { ImportStructureModel }    from './typings/imports.import-structure';
import { JsonService }             from '../core/services/json.service';

@Injectable({
  providedIn: 'root'
})
export class ImportsService implements OnDestroy {
  phoneColumns : Array<string> = [
    'contact_phone',
    'phone',
    'mobile',
    'fax',
    'contact1_phone',
    'contact2_phone'
  ];

  progress: any;
  modal   : NgbModalRef;
  xhr     : XMLHttpRequest

  isImporting = false;

  constructor (
    private apiService      : ApiService,
    private jsonService     : JsonService,
    private modalService    : NgbModal,
    private translateService: TranslateService
  ) {}

  cancelImport () : ng.IPromise<any> {
    return this.apiService.get('/imports/cancel_import')
    .then(() => {
      this.modal.close();

      return this.translateService.instant('JS_SPACE.IMPORTS.IMPORT_CANCELED');
    });
  }

  isPhoneColumn ( columnName : string ) : boolean {
    return this.phoneColumns.includes(columnName);
  }

  ngOnDestroy () : void {
    this.stopProgress();

    if ( this.xhr ) {
      this.xhr.removeAllListeners('abort');
      this.xhr.removeAllListeners('error');
      this.xhr.removeAllListeners('loadend');
    }
  }

  showProgress () : ng.IPromise<any> {
    this.modal = this.modalService.open(ImportProgressComponent);

    this.modal.componentInstance.percentage = 0;

    this.modal.closed.subscribe(() => {
      this.stopProgress();
    });

    this.modal.dismissed.subscribe(() => {
      this.stopProgress();
    });

    this.modal.shown.subscribe(() => {
      this.isImporting = true;
    });

    return this.modal.result;
  }

  start ( importUrl : string, file : File, importStructure : Array<ImportStructureModel>, hasHeaders = true, additionalFormFields : Array<ImportFormFieldModel> = [] ) : ng.IPromise<any> {
    return new Promise(( resolve, reject ) => {
      this.showProgress()
      .catch(() => resolve(this.cancelImport()));

      const stringedStructure = JSON.stringify(importStructure);

      const form = new FormData;

      // Specify whether there are column names included
      // in the first row so that row can be skipped if needed
      form.append('has_headers', hasHeaders.toString());

      // Pass the stringed structure
      form.append('import_structure', stringedStructure);

      // Now finally the file
      form.append('csv_file', file);

      if ( additionalFormFields.length ) {
        additionalFormFields.forEach(additionalFormField => {
          form.append(additionalFormField.name, additionalFormField.value);
        });
      }

      this.xhr = new XMLHttpRequest;

      this.xhr.addEventListener('abort', ( e ) => {
        this.modal.close();

        reject(this.translateService.instant('JS_SPACE.IMPORTS.FAILED'));
      });

      this.xhr.addEventListener('error', ( e ) => {
        this.modal.close();

        reject(this.translateService.instant('JS_SPACE.IMPORTS.FAILED'));
      });

      this.xhr.addEventListener('loadend', ( e : any ) => {
        this.modal.close();

        if ( e.target.status !== 200 ) {
          reject(this.jsonService.parse(e.target.responseText));
        }
        else {
          resolve(e.target.responseText);
        }
      });

      // Start the call
      this.xhr.open('POST', `${ this.apiService.getEndpoint() }${ importUrl }`, true);

      this.xhr.withCredentials = true;

      // Send the formdata
      this.xhr.send(form);

      this.progress = setInterval(() => {
        this.apiService.get('/imports/import_progress')
        .then(progress => this.updateProgress(+( parseFloat(progress) * 100 ).toFixed(1)));
      }, 2000);
    });
  }

  stopProgress () : void {
    this.isImporting = false;

    if ( this.progress ) {
      clearInterval(this.progress);
    }
  }

  updateProgress ( percentage : number ) : void {
    if ( this.isImporting ) {
      this.modal.componentInstance.percentage = percentage;
    }
  }
}
